// Calculator registry for systematic internal linking and SEO
// This registry helps fix orphan pages by ensuring all calculators are properly linked

/**
 * Registry of all available calculators for internal linking
 * Used to generate navigation, footer links, and related calculator suggestions
 */
export const CALCULATOR_REGISTRY = [
  // Main salary calculators
  {
    id: 'salary-calculator',
    name: 'UK Salary Calculator',
    description: 'Calculate UK salary with tax breakdown and visual bands',
    url: '/salary-calculator',
    category: 'main',
    priority: 10
  },
  {
    id: 'salary-comparison',
    name: 'Salary Comparison Tool',
    description: 'Compare two UK salaries side by side',
    url: '/salary-comparison',
    category: 'main',
    priority: 9
  },
  
  // Popular salary-specific pages
  {
    id: 'salary-20000',
    name: '£20,000 After Tax',
    description: 'Calculate £20,000 UK salary breakdown',
    url: '/20000-after-tax-uk',
    category: 'salary-specific',
    salary: 20000,
    priority: 8
  },
  {
    id: 'salary-25000',
    name: '£25,000 After Tax',
    description: 'Calculate £25,000 UK salary breakdown',
    url: '/25000-after-tax-uk',
    category: 'salary-specific',
    salary: 25000,
    priority: 8
  },
  {
    id: 'salary-30000',
    name: '£30,000 After Tax',
    description: 'Calculate £30,000 UK salary breakdown',
    url: '/30000-after-tax-uk',
    category: 'salary-specific',
    salary: 30000,
    priority: 8
  },
  {
    id: 'salary-35000',
    name: '£35,000 After Tax',
    description: 'Calculate £35,000 UK salary breakdown',
    url: '/35000-after-tax-uk',
    category: 'salary-specific',
    salary: 35000,
    priority: 7
  },
  {
    id: 'salary-40000',
    name: '£40,000 After Tax',
    description: 'Calculate £40,000 UK salary breakdown',
    url: '/40000-after-tax-uk',
    category: 'salary-specific',
    salary: 40000,
    priority: 9
  },
  {
    id: 'salary-45000',
    name: '£45,000 After Tax',
    description: 'Calculate £45,000 UK salary breakdown',
    url: '/45000-after-tax-uk',
    category: 'salary-specific',
    salary: 45000,
    priority: 7
  },
  {
    id: 'salary-50000',
    name: '£50,000 After Tax',
    description: 'Calculate £50,000 UK salary breakdown',
    url: '/50000-after-tax-uk',
    category: 'salary-specific',
    salary: 50000,
    priority: 9
  },
  {
    id: 'salary-55000',
    name: '£55,000 After Tax',
    description: 'Calculate £55,000 UK salary breakdown',
    url: '/55000-after-tax-uk',
    category: 'salary-specific',
    salary: 55000,
    priority: 6
  },
  {
    id: 'salary-60000',
    name: '£60,000 After Tax',
    description: 'Calculate £60,000 UK salary breakdown',
    url: '/60000-after-tax-uk',
    category: 'salary-specific',
    salary: 60000,
    priority: 7
  },
  {
    id: 'salary-70000',
    name: '£70,000 After Tax',
    description: 'Calculate £70,000 UK salary breakdown',
    url: '/70000-after-tax-uk',
    category: 'salary-specific',
    salary: 70000,
    priority: 6
  },
  {
    id: 'salary-80000',
    name: '£80,000 After Tax',
    description: 'Calculate £80,000 UK salary breakdown',
    url: '/80000-after-tax-uk',
    category: 'salary-specific',
    salary: 80000,
    priority: 6
  },
  {
    id: 'salary-100000',
    name: '£100,000 After Tax',
    description: 'Calculate £100,000 UK salary breakdown',
    url: '/100000-after-tax-uk',
    category: 'salary-specific',
    salary: 100000,
    priority: 7
  },
  
  // Specialized tools
  {
    id: 'take-home-simple',
    name: 'Simple Take Home Calculator',
    description: 'Quick UK take home pay calculator',
    url: '/tools/take-home-simple',
    category: 'tools',
    priority: 5
  }
];

/**
 * Get calculators by category
 * @param {string} category - Category to filter by
 * @returns {array} Filtered calculator list
 */
export const getCalculatorsByCategory = (category) => {
  return CALCULATOR_REGISTRY.filter(calc => calc.category === category)
    .sort((a, b) => b.priority - a.priority);
};

/**
 * Get top calculators for navigation/footer
 * @param {number} limit - Number of calculators to return
 * @returns {array} Top calculators sorted by priority
 */
export const getTopCalculators = (limit = 8) => {
  return CALCULATOR_REGISTRY
    .sort((a, b) => b.priority - a.priority)
    .slice(0, limit);
};

/**
 * Get related calculators for a given salary amount
 * @param {number} salary - Current salary amount
 * @param {number} limit - Number of related calculators to return
 * @returns {array} Related salary calculators
 */
export const getRelatedSalaryCalculators = (salary, limit = 5) => {
  const salarySpecific = getCalculatorsByCategory('salary-specific');
  const current = salarySpecific.find(calc => calc.salary === salary);
  
  if (!current) {
    return salarySpecific.slice(0, limit);
  }
  
  // Get calculators with salaries close to the current one
  const related = salarySpecific
    .filter(calc => calc.salary !== salary)
    .sort((a, b) => {
      const diffA = Math.abs(a.salary - salary);
      const diffB = Math.abs(b.salary - salary);
      return diffA - diffB;
    })
    .slice(0, limit);
    
  return related;
};

/**
 * Get main navigation calculators
 * @returns {array} Main calculators for navigation
 */
export const getMainCalculators = () => {
  return getCalculatorsByCategory('main');
};

/**
 * Get all salary-specific calculators
 * @returns {array} All salary-specific calculators
 */
export const getAllSalaryCalculators = () => {
  return getCalculatorsByCategory('salary-specific');
};

/**
 * Find calculator by URL
 * @param {string} url - URL to find
 * @returns {object|null} Calculator object or null if not found
 */
export const findCalculatorByUrl = (url) => {
  return CALCULATOR_REGISTRY.find(calc => calc.url === url) || null;
};